import React, { useEffect, useState } from "react";

import "./RBACPage.css";

import { getRBACAPI, updatePermissionAPI } from "../../api/rbac";

import { useAuth } from "../../contexts/AuthContext";

import useAlert from "../../hooks/useAlert";

import ActionDropdown from "../../components/ActionDropDown/ActionDropdown";

import Button from "../../components/Button";

const RBACPage = () => {
  const { user } = useAuth();

  const { showAlert } = useAlert();

  const [roles, setRoles] = useState([]);
  const [enterprizes, setEnterprizes] = useState([]);
  const [selectedEnterprizeId, setSelectedEnterprizeId] = useState("");
  const [selectedEnterprize, setSelectedEnterprize] = useState(null);

  const [pages, setPages] = useState([]);

  const [selectedRole, setSelectedRole] = useState(null);

  /**
   * LOAD ONLY ROLES AND PAGES (NO AUTO SELECT)
   */

  useEffect(() => {
    fetchRBAC("", selectedEnterprizeId);
  }, [selectedEnterprizeId]);

  useEffect(() => {
    if (!user?.is_super_admin && user?.enterprize_id) {
      setSelectedEnterprizeId(String(user.enterprize_id));
    }
  }, []);

  useEffect(() => {
    if (!enterprizes.length || !selectedEnterprizeId) {
      setSelectedEnterprize(null);
      return;
    }
    const matched = enterprizes.find(
      (ent) => String(ent.enterprize_id) === String(selectedEnterprizeId),
    );
    setSelectedEnterprize(matched || null);
  }, [enterprizes, selectedEnterprizeId]);

  /**
   * FETCH RBAC
   */

  const fetchRBAC = async (roleId = "", enterprizeId = "") => {
    try {
      const res = await getRBACAPI(roleId, enterprizeId);

      setEnterprizes(res.data.data.enterprizes || []);
      setRoles(res.data.data.roles || []);

      setPages(res.data.data.pages || []);
    } catch (err) {
      console.error(err);

      showAlert("error", "Failed to load RBAC");
    }
  };

  /**
   * ROLE SELECT
   */

  const handleRoleSelect = async (role) => {
    setSelectedRole(role);

    // fetch permission of selected role

    fetchRBAC(role.role_id, selectedEnterprizeId);
  };

  /**
   * VALIDATE ROLE
   */

  const validateRole = () => {
    if (!selectedRole) {
      showAlert("warning", "Please select role first");

      return false;
    }

    return true;
  };

  /**
   * SWITCH
   */

  const handleSwitch = async (pageId, field, value) => {
    if (!validateRole()) return;

    try {
      const page = pages.find((p) => p.page_id === pageId);

      await updatePermissionAPI({
        role_id: selectedRole.role_id,
        enterprize_id: selectedEnterprizeId || undefined,

        page_id: pageId,

        can_view: field === "can_view" ? value : page.can_view,

        can_create: field === "can_create" ? value : page.can_create,

        can_edit: field === "can_edit" ? value : page.can_edit,

        can_delete: field === "can_delete" ? value : page.can_delete,
      });

      showAlert("success", "Permission updated");

      fetchRBAC(selectedRole.role_id, selectedEnterprizeId);
    } catch (err) {
      console.error(err);

      showAlert("error", "Update failed");
    }
  };

  /**
   * ENABLE / DISABLE ALL
   */

  const handleAll = async (pageId, enable) => {
    if (!validateRole()) return;

    try {
      await updatePermissionAPI({
        role_id: selectedRole.role_id,
        enterprize_id: selectedEnterprizeId || undefined,

        page_id: pageId,

        can_view: enable,

        can_create: enable,

        can_edit: enable,

        can_delete: enable,
      });

      showAlert("success", "Permission updated");

      fetchRBAC(selectedRole.role_id, selectedEnterprizeId);
    } catch (err) {
      console.error(err);

      showAlert("error", "Update failed");
    }
  };

  /**
   * ROLE DROPDOWN
   */

  const roleItems = roles.map((role) => ({
    label: role.role_name,

    icon: "person",

    onClick: () => handleRoleSelect(role),
  }));

  const enterprizeItems = enterprizes.map((ent) => ({
    label: ent.enterprize_name,
    icon: "building",
    onClick: () => {
      setSelectedEnterprizeId(String(ent.enterprize_id));
      setSelectedEnterprize(ent);
      setSelectedRole(null);
    },
  }));

  return (
    <div className="rbac-container">
      <h3>RBAC Management</h3>

      {/* DROPDOWN */}

      <div className="mb-3 d-flex justify-content-end">
        {user.is_super_admin && (
          <div className="me-2">
            <ActionDropdown items={enterprizeItems}>
              <Button variant="secondary" className="rbac-enterprise-select">
                {selectedEnterprize
                  ? selectedEnterprize.enterprize_name
                  : "Select Enterprize"}
                <i className="ms-3 bi bi-chevron-down" />
              </Button>
            </ActionDropdown>
          </div>
        )}

        {user.is_super_admin && (
          <ActionDropdown items={roleItems}>
            <Button variant="secondary">
              {selectedRole ? selectedRole.role_name : "Select Role"}

              <i className="ms-3 bi bi-chevron-down" />
            </Button>
          </ActionDropdown>
        )}
      </div>

      {/* TABLE */}

      <table className="rbac-table">
        <thead>
          <tr>
            <th>Page</th>

            <th>All</th>

            <th>View</th>

            <th>Create</th>

            <th>Edit</th>

            <th>Delete</th>
          </tr>
        </thead>

        <tbody>
          {pages.map((page) => {
            const allEnabled =
              page.can_view &&
              page.can_create &&
              page.can_edit &&
              page.can_delete;

            return (
              <tr key={page.page_id}>
                <td>{page.page_name}</td>

                {/* ALL BUTTON */}

                <td>
                  <Button
                    variant={allEnabled ? "danger" : "success"}
                    size="small"
                    onClick={() => handleAll(page.page_id, !allEnabled)}
                  >
                    {allEnabled ? "Disable" : "Enable"}
                  </Button>
                </td>

                {/* SWITCH */}

                {["can_view", "can_create", "can_edit", "can_delete"].map(
                  (field) => (
                    <td key={field}>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={page[field]}
                          onChange={(e) =>
                            handleSwitch(
                              page.page_id,

                              field,

                              e.target.checked,
                            )
                          }
                        />

                        <span className="slider round"></span>
                      </label>
                    </td>
                  ),
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default RBACPage;
